import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { TemaProvider } from '@/lib/TemaContext';
import ScrollToTop from './components/ScrollToTop';
import Layout from './components/Layout';
import { SiteLoadingProvider } from './components/SiteLoadingScreen';
import RouteTransitionScreen from './components/RouteTransitionScreen';
// Add page imports here
import Home from './pages/Home';
import { findRouteRedirect, PublicRoutesProvider, usePublicRoutes } from '@/lib/publicRoutes';
import { parseIntranetHash } from '@/lib/intranetNavigation';

const Exames = lazy(() => import('./pages/Exames'));
const ExameDetail = lazy(() => import('./pages/ExameDetail'));
const Agendar = lazy(() => import('./pages/Agendar'));
const Portal = lazy(() => import('./pages/Portal'));
const Convenios = lazy(() => import('./pages/Convenios'));
const Sobre = lazy(() => import('./pages/Sobre'));
const Contato = lazy(() => import('./pages/Contato'));
const Faq = lazy(() => import('./pages/Faq'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const CorpoClinico = lazy(() => import('./pages/CorpoClinico'));
const MedicoPerfil = lazy(() => import('./pages/MedicoPerfil'));
const Carreiras = lazy(() => import('./pages/Carreiras'));
const Vagas = lazy(() => import('./pages/Vagas'));
const Termo = lazy(() => import('./pages/Termo'));
const Admin = lazy(() => import('./pages/Admin'));
const Intra = lazy(() => import('./pages/Intra'));
const CertificateValidation = lazy(() => import('./pages/CertificateValidation'));
const AvaliacaoVaga = lazy(() => import('./pages/AvaliacaoVaga'));

const isStandaloneApp = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

function IntranetAppLaunchRedirect() {
  const location = useLocation();
  const isIntranetDeepLink = Boolean(location.hash) && parseIntranetHash(location.hash).module !== 'inicio';

  // Older home-screen shortcuts may have been saved with the root URL. Once
  // opened as an app, keep them in the intranet without affecting web visits.
  // Root-level intranet hashes (e.g. /#faturamento/credenciamentos) also belong
  // to the intranet, so they survive a refresh instead of falling back to Home.
  if (location.pathname === '/' && (isStandaloneApp() || isIntranetDeepLink)) {
    return <Navigate replace to={`/intra${location.search}${location.hash}`} />;
  }

  return null;
}



const PublicApp = () => {
  const location = useLocation();
  const { pages, redirects, path } = usePublicRoutes();

  const redirect = findRouteRedirect(location.pathname, pages, redirects);
  if (redirect) return <Navigate replace to={`${redirect}${location.search}${location.hash}`} />;

  return (
    <Suspense fallback={<RouteTransitionScreen />}>
    <Routes>
      <Route path="/vagas/avaliacao" element={<AvaliacaoVaga />} />
      <Route element={<Layout />}>
        <Route path={path('home')} element={<Home />} />
        <Route path={path('exams')} element={<Exames />} />
        <Route path={`${path('exams').replace(/\/$/, '')}/:id`} element={<ExameDetail />} />
        <Route path={path('schedule')} element={<Agendar />} />
        <Route path={path('portal')} element={<Portal />} />
        <Route path={path('insurance')} element={<Convenios />} />
        <Route path={path('about')} element={<Sobre />} />
        <Route path={path('contact')} element={<Contato />} />
        <Route path={path('faq')} element={<Faq />} />
        <Route path={path('blog')} element={<Blog />} />
        <Route path={`${path('blog').replace(/\/$/, '')}/:id`} element={<BlogPost />} />
        <Route path={path('doctors')} element={<CorpoClinico />} />
        <Route path={`${path('doctors').replace(/\/$/, '')}/:slug`} element={<MedicoPerfil />} />
        <Route path={path('careers')} element={<Carreiras />} />
        <Route path="/vagas" element={<Vagas />} />
        <Route path="/termos/:slug" element={<Termo />} />
      </Route>
      <Route path="/admin" element={<Admin />} />
      <Route path="/intra/*" element={<Intra />} />
      <Route path="/certificados/:code" element={<CertificateValidation />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <TemaProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <PublicRoutesProvider>
            <SiteLoadingProvider>
              <IntranetAppLaunchRedirect />
              <ScrollToTop />
              <PublicApp />
            </SiteLoadingProvider>
          </PublicRoutesProvider>
        </Router>
        <Toaster />
      </QueryClientProvider>
    </TemaProvider>
  )
}

export default App
